import { useMemo } from 'react';
import { GLOBAL_TRANSITION_PORT } from './ellipsis.tsx';
import {
	type Point,
	type DiagramTransition,
	type PortInfo,
	type StatefulDiagramTransition,
	toPortKey,
	toPoint,
} from './toPortInfo.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import {
	transitionMatchesReference,
	isHighlightedTransition,
	findPort,
	isTransitionRerouting,
	updateTransitionPortIfRerouting,
	isTransitionOverlapping,
	isAnyOverlappingTransitionHovered,
	isDirectOrSharedTransitionWithDefinedPorts,
} from './isEveryPropertyDefined.tsx';
import { manhattanPathAndLabelPosition } from './calculateLabelPosition.tsx';
import { calculateOverlappingLabelPlacement } from './calculateOverlappingLabelPlacement.tsx';
import { getLabelPlacement } from './getLabelPlacement.tsx';
import type { ReroutingTransition } from './ReroutingTransitionValid.types.tsx';
import type { TransitionsWithDefaultPorts } from './useAutoLayout.tsx';
import { getSharedTransitionLayout } from './getConvergingPortLayout.tsx';

export const ICON_SET_CONTAINER_Y_OFFSET = 35;

export interface Options {
	transitions: DiagramTransition[];
	selectedItem: ItemReference | undefined;
	targetedItem: ItemReference | undefined;
	hoveredItem: ItemReference | undefined;
	ports: Record<string, PortInfo>;
	reroutingTransition: ReroutingTransition;
	transitionsWithDefaultPorts: TransitionsWithDefaultPorts;
}

export const useStatefulTransitions = ({
	transitions,
	selectedItem,
	targetedItem,
	hoveredItem,
	ports,
	reroutingTransition,
	transitionsWithDefaultPorts,
}: Options): StatefulDiagramTransition[] => {
	const transitionLabelPlacementsNew = useMemo(
		() => calculateOverlappingLabelPlacement(transitions),
		[transitions],
	);

	const statefulTransitions = transitions.map((baseTransition): StatefulDiagramTransition => {
		let isOverlapping = false;
		let isOverlapHovered = false;

		// use default transition data if it exists and the base transition is missing ports
		const transition =
			(baseTransition.type === 'shared' || baseTransition.type === 'direct') &&
			!isDirectOrSharedTransitionWithDefinedPorts(baseTransition) &&
			transitionsWithDefaultPorts[baseTransition.id]
				? transitionsWithDefaultPorts[baseTransition.id]
				: baseTransition;

		isOverlapping = isTransitionOverlapping(transition.id, transitionLabelPlacementsNew);
		isOverlapHovered = isAnyOverlappingTransitionHovered(
			transition.id,
			hoveredItem?.id,
			transitionLabelPlacementsNew,
		);

		const transitionFlags = {
			isSelected: transitionMatchesReference(transition, selectedItem),
			isTargeted: transitionMatchesReference(transition, targetedItem),
			isHovered: transitionMatchesReference(transition, hoveredItem),
			isHighlighted: isHighlightedTransition(transition, selectedItem, hoveredItem),
			isRerouting: isTransitionRerouting(transition, reroutingTransition),
			isOverlapping,
			isOverlapHovered,
		};

		if (transition.type === 'global-looped') {
			// TODO HIX-5551 implement label positions
			let labelPosition: Point | undefined;

			return {
				...transition,
				...transitionFlags,
				labelPosition,
			};
		}

		if (transition.type === 'global') {
			const targetPort = ports[toPortKey(transition.target, GLOBAL_TRANSITION_PORT)];

			let labelPosition: Point | undefined;

			if (targetPort?.position) {
				labelPosition = toPoint(
					targetPort.position.x,
					targetPort.position.y + ICON_SET_CONTAINER_Y_OFFSET,
				);
			}

			return {
				...transition,
				...transitionFlags,
				labelPosition,
				targetPort,
			};
		}

		if (transition.type === 'shared') {
			return {
				...transition,
				...transitionFlags,
				...getSharedTransitionLayout(
					transition,
					ports,
					transitionLabelPlacementsNew,
					reroutingTransition,
				),
			};
		}

		// direct transition
		let sourcePort = findPort(ports, transition.source, transition.sourceIndex);

		sourcePort = updateTransitionPortIfRerouting(
			sourcePort,
			'source',
			transition,
			reroutingTransition,
		);

		let targetPort = findPort(ports, transition.target, transition.targetIndex);

		targetPort = updateTransitionPortIfRerouting(
			targetPort,
			'target',
			transition,
			reroutingTransition,
		);

		const labelPlacement = getLabelPlacement(transition.id, transitionLabelPlacementsNew);

		if (sourcePort && targetPort) {
			const { path, labelPosition } = manhattanPathAndLabelPosition(sourcePort, targetPort, {
				fractionOfPath: labelPlacement,
				endsPadding: 0.25,
			});

			return {
				...transition,
				...transitionFlags,
				sourcePort,
				targetPort,
				path,
				labelPosition,
			};
		}

		return {
			...transition,
			...transitionFlags,
			sourcePort,
			targetPort,
			path: undefined,
			labelPosition: undefined,
		};
	});

	return statefulTransitions;
};
